import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { formatINR, formatINRCompact, formatDateTime } from "../lib/format";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Link } from "react-router-dom";
import {
    Wallet,
    TrendingUp,
    Users as UsersIcon,
    AlertCircle,
    IndianRupee,
    PiggyBank,
    ArrowUpRight,
    BadgeIndianRupee,
} from "lucide-react";

function KPI({ icon: Icon, label, value, tone = "blue", testid, sub }) {
    const toneMap = {
        blue: "from-blue-50 to-white text-blue-900",
        green: "from-emerald-50 to-white text-emerald-700",
        amber: "from-amber-50 to-white text-amber-700",
        red: "from-rose-50 to-white text-rose-700",
        slate: "from-slate-50 to-white text-slate-700",
    };
    return (
        <Card
            data-testid={testid}
            className={`relative overflow-hidden rounded-2xl border-slate-100 p-5 card-hover bg-gradient-to-br ${toneMap[tone]}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">
                        {label}
                    </div>
                    <div className="num text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
                        {value}
                    </div>
                    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
                </div>
                <div className="h-10 w-10 rounded-xl bg-white shadow-sm grid place-items-center">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
            </div>
        </Card>
    );
}

export default function Dashboard() {
    const { t } = useLang();
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [insights, setInsights] = useState(null);
    const [reminderStats, setReminderStats] = useState(null);
    const [pendingInt, setPendingInt] = useState(null);

    useEffect(() => {
        api.get("/dashboard/stats").then((r) => setStats(r.data));
        api.get("/dashboard/recent-payments").then((r) => setRecent(r.data));
        api.get("/dashboard/profit-insights").then((r) => setInsights(r.data));
        api.get("/reminders/stats").then((r) => setReminderStats(r.data));
        api.get("/dashboard/pending-interest").then((r) => setPendingInt(r.data));
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                        {t("dashboard")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Welcome back, <span className="font-semibold">{user?.name}</span>
                    </p>
                </div>
                <Badge className="bg-blue-50 text-blue-900 border-blue-100 hover:bg-blue-50" data-testid="role-badge">
                    {user?.role?.toUpperCase()}
                </Badge>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <KPI
                    testid="kpi-active-loans"
                    icon={Wallet}
                    label={t("active_loans")}
                    value={stats?.active_loans ?? "—"}
                    tone="blue"
                />
                <KPI
                    testid="kpi-total-customers"
                    icon={UsersIcon}
                    label={t("total_customers")}
                    value={stats?.total_customers ?? "—"}
                    tone="slate"
                />
                <KPI
                    testid="kpi-pending-approvals"
                    icon={AlertCircle}
                    label={t("pending_approvals")}
                    value={stats?.pending_approvals ?? "—"}
                    tone="amber"
                />
                <KPI
                    testid="kpi-today-collection"
                    icon={IndianRupee}
                    label={t("total_collections")}
                    value={stats ? formatINRCompact(stats.today_collection) : "—"}
                    tone="green"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
                <KPI
                    testid="kpi-total-outstanding"
                    icon={BadgeIndianRupee}
                    label={t("total_outstanding")}
                    value={stats ? formatINRCompact(stats.total_outstanding) : "—"}
                    tone="blue"
                />
                <KPI
                    testid="kpi-total-disbursed"
                    icon={PiggyBank}
                    label={t("total_disbursed")}
                    value={stats ? formatINRCompact(stats.total_disbursed) : "—"}
                    tone="slate"
                />
                <KPI
                    testid="kpi-total-profit"
                    icon={TrendingUp}
                    label={t("profit")}
                    value={stats ? formatINRCompact(stats.total_interest_earned) : "—"}
                    tone="green"
                />
            </div>

            {/* Current Month Pending Interest Tracker */}
            <Card
                className="rounded-2xl border-slate-100 p-5 sm:p-6"
                data-testid="pending-interest-card"
            >
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                            Current Month Pending Interest
                        </div>
                        <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900">
                            {pendingInt?.month || "This month"}
                        </h2>
                    </div>
                    <Badge
                        data-testid="pending-interest-progress-badge"
                        className={`text-sm font-bold border ${
                            (pendingInt?.progress_pct ?? 0) >= 80
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : (pendingInt?.progress_pct ?? 0) >= 50
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}
                    >
                        {pendingInt ? `${pendingInt.progress_pct}% collected` : "—"}
                    </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="rounded-xl bg-blue-50 border border-blue-100 px-3 py-3" data-testid="pending-interest-expected">
                        <div className="text-[10px] uppercase tracking-wider text-blue-900 font-semibold">
                            Expected
                        </div>
                        <div className="num text-xl font-bold text-slate-900 mt-0.5">
                            {pendingInt ? formatINR(pendingInt.expected) : "—"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            on {pendingInt?.active_loans ?? 0} active loan
                            {(pendingInt?.active_loans ?? 0) === 1 ? "" : "s"}
                        </div>
                    </div>
                    <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-3" data-testid="pending-interest-received">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
                            Received
                        </div>
                        <div className="num text-xl font-bold text-slate-900 mt-0.5">
                            {pendingInt ? formatINR(pendingInt.received) : "—"}
                        </div>
                        <div className="text-[11px] text-emerald-700 mt-0.5">
                            collected this month
                        </div>
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-3" data-testid="pending-interest-pending">
                        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">
                            Pending
                        </div>
                        <div className="num text-xl font-bold text-slate-900 mt-0.5">
                            {pendingInt ? formatINR(pendingInt.pending) : "—"}
                        </div>
                        <div className="text-[11px] text-amber-700 mt-0.5">
                            to be collected
                        </div>
                    </div>
                </div>

                <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all ${
                            (pendingInt?.progress_pct ?? 0) >= 80
                                ? "bg-emerald-500"
                                : (pendingInt?.progress_pct ?? 0) >= 50
                                  ? "bg-amber-500"
                                  : "bg-rose-500"
                        }`}
                        style={{ width: `${Math.min(100, Math.max(0, pendingInt?.progress_pct ?? 0))}%` }}
                    />
                </div>

                <div
                    className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3"
                    data-testid="pending-interest-carryforward"
                >
                    <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                            Previous Pending Interest
                        </div>
                        <div
                            className="num text-base font-bold text-amber-700 mt-0.5"
                            data-testid="pending-interest-previous"
                        >
                            {pendingInt ? formatINR(pendingInt.previous_pending ?? 0) : "—"}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                            carry-forward from earlier months
                        </div>
                    </div>
                    <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">
                            Grand Total Interest Pending
                        </div>
                        <div
                            className="num text-base font-bold text-rose-800 mt-0.5"
                            data-testid="pending-interest-grand-total"
                        >
                            {pendingInt ? formatINR(pendingInt.grand_total_pending ?? 0) : "—"}
                        </div>
                        <div className="text-[10px] text-rose-700/70 mt-0.5">
                            this month + previous pending
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100">
                <div className="flex items-center justify-between p-5 pb-2">
                    <h2 className="font-display text-lg font-bold text-slate-900">
                        {t("recent_payments")}
                    </h2>
                    <Link
                        to="/collections"
                        className="text-xs text-blue-900 font-semibold inline-flex items-center gap-1"
                        data-testid="view-all-collections"
                    >
                        {t("view_all")} <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
                <div className="divide-y divide-slate-100">
                    {recent.length === 0 && (
                        <div className="p-6 text-center text-slate-400 text-sm">{t("no_data")}</div>
                    )}
                    {recent.map((p) => (
                        <div key={p.id} className="px-5 py-3.5 flex items-center justify-between">
                            <div>
                                <div className="font-semibold text-slate-900 text-sm">
                                    {p.customer_name}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {formatDateTime(p.payment_date)} · {p.mode?.toUpperCase()}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="num font-bold text-slate-900">
                                    {formatINR(p.total)}
                                </div>
                                {p.type === "loan" && p.principal_paid > 0 && (
                                    <div className="text-[11px] text-emerald-700">
                                        {formatINR(p.principal_paid)} {t("deducted_from_principal")}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Profit Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                <Card className="rounded-2xl border-slate-100 p-5" data-testid="dash-reminders-card">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-base font-bold text-slate-900">
                            WhatsApp Reminders
                        </h2>
                        <Link
                            to="/reminders"
                            data-testid="dash-reminders-link"
                            className="text-xs text-blue-900 font-semibold inline-flex items-center gap-1"
                        >
                            Open <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-3">
                            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">
                                Sent Today
                            </div>
                            <div className="num text-2xl font-bold text-slate-900 mt-1">
                                {reminderStats?.today_sent ?? "—"}
                            </div>
                        </div>
                        <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-3">
                            <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">
                                Pending Customers
                            </div>
                            <div className="num text-2xl font-bold text-slate-900 mt-1">
                                {reminderStats?.pending_customers ?? "—"}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="rounded-2xl border-slate-100 p-5" data-testid="profit-insights-collectors">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-base font-bold text-slate-900">
                            Top Collectors · {insights?.month || ""}
                        </h2>
                        <TrendingUp className="h-4 w-4 text-emerald-600" />
                    </div>
                    {!insights || insights.top_collectors.length === 0 ? (
                        <div className="text-sm text-slate-400">No collections this month yet</div>
                    ) : (
                        <div className="space-y-2">
                            {insights.top_collectors.map((s, idx) => (
                                <div key={s.staff || `collector-${idx}`} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2.5">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`h-7 w-7 rounded-lg grid place-items-center text-xs font-bold ${idx === 0 ? "bg-amber-100 text-amber-700" : "bg-white text-slate-600"}`}>
                                            #{idx + 1}
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-900">{s.staff}</div>
                                            <div className="text-[11px] text-slate-500">{s.count} collections</div>
                                        </div>
                                    </div>
                                    <div className="num font-bold text-slate-900">{formatINR(s.total)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>

                <Card className="rounded-2xl border-slate-100 p-5" data-testid="profit-insights-nearest">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display text-base font-bold text-slate-900">
                            Loans Closest to Closure
                        </h2>
                        <Wallet className="h-4 w-4 text-blue-900" />
                    </div>
                    {!insights || insights.nearest_closure.length === 0 ? (
                        <div className="text-sm text-slate-400">No active loans</div>
                    ) : (
                        <div className="space-y-2">
                            {insights.nearest_closure.map((l) => (
                                <Link
                                    to={`/loans/${l.loan_id}`}
                                    key={l.loan_id}
                                    data-testid={`closure-loan-${l.loan_id}`}
                                    className="block bg-slate-50 hover:bg-slate-100 rounded-xl px-3 py-2.5"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-sm font-semibold text-slate-900">{l.customer_name}</div>
                                            <div className="text-[11px] text-slate-500 font-mono">{l.loan_code} · {l.percent_paid}% paid</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="num font-bold text-blue-900">{formatINR(l.outstanding)}</div>
                                            <div className="text-[10px] text-slate-500">outstanding</div>
                                        </div>
                                    </div>
                                    <div className="mt-1.5 h-1.5 bg-white rounded-full overflow-hidden">
                                        <div className="h-full bg-emerald-500" style={{ width: `${l.percent_paid}%` }} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
